/**
 * SocketIO Types
 *
 * Type definitions for the SocketIO service.
 * Aligned with v2 backend socket events.
 */

// ============================================================================
// Socket Events Enum (v2 backend event names)
// ============================================================================

export enum SocketIOEvent {
  // Call namespace events (/call)
  CallCreated = 'created',
  CallStatus = 'status', // Also used in /user namespace — filter by namespace
  SummaryCreated = 'summary_created',

  // User namespace events (/user)
  // UserStatus uses 'status' — same string as CallStatus, distinguished by namespace
  UserStatus = 'status',

  // Notification namespace events (/notification)
  NewNotification = 'new_notification',
  ReadNotification = 'read',
}

// ============================================================================
// Socket Namespaces
// ============================================================================

export enum SocketIONamespace {
  Notification = '/notification',
  User = '/user',
  Call = '/call',
}

// ============================================================================
// v2 Backend Event Payload Types
// ============================================================================

/**
 * Participant in a call (caller or callee)
 * Used in call.created and queue paginated API responses.
 */
export interface V2ParticipantResponse {
  id: number;
  name: string;
  photo_url?: string;
  nric?: string;
  role?: string;
}

/**
 * Case information attached to a call
 */
export interface V2CaseInfoResponse {
  case_id: number;
  case_external_id?: string;
  loan_id?: string;
  tags?: string[];
  tier?: string;
  ammount_due?: string;
  case_status?: string;
  last_payment_date?: string;
  next_case_id?: number;
  dpd?: number;
}

/**
 * Forward-from info — the participant who forwarded the call
 */
export interface V2ForwardFromResponse {
  id: number;
  name: string;
}

/**
 * Call response — emitted by call.created and used in API responses
 */
export interface V2CallResponse {
  id: number;
  type: string;
  name?: string;
  role?: string;
  relationship?: string;
  time_start?: string;
  time_end?: string;
  status: string;
  callees: V2ParticipantResponse[];
  caller: V2ParticipantResponse;
  forward_from?: V2ForwardFromResponse;
  has_transcript: boolean;
  is_fallback: boolean;
  has_call_detail: boolean;
  case_info?: V2CaseInfoResponse;
  sequence_number?: number;
}

// ============================================================================
// Socket Event Data Types
// ============================================================================

export interface SocketIOEventData<T = unknown> {
  event: string;
  data: T;
  namespace: string;
  timestamp: Date;
}

/**
 * /user namespace — status event payload
 */
export interface UserStatusEventData {
  user_id: number;
  status: string;
}

/**
 * /call namespace — status event payload (call status changed)
 */
export interface CallStatusEventData {
  call_id: number;
  status: string;
}

/**
 * /call namespace — summary_created event payload
 */
export interface CallSummaryCreatedEventData {
  call_id: number;
}

/**
 * /notification namespace — new_notification event payload
 */
export interface NotificationEventData {
  id: number;
  notification_id: number;
  type: string;
  category: string;
  title: string;
  message: string;
  data?: Record<string, unknown>;
  created_at: string;
}

/**
 * /notification namespace — read event payload
 */
export interface NotificationReadEventData {
  notification_ids: number[];
}

// ============================================================================
// Socket Connection State
// ============================================================================

export type SocketIOConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

export interface SocketIOState {
  status: SocketIOConnectionStatus;
  error: string | null;
  connectedNamespaces: string[];
}
